/** @format */
import React, { useEffect } from "react";
import Cookies from "js-cookie";
import {
  getSessionTokenKlippa,
  getKlippaSession,
  webhookKlippa,
} from "../../../../../API/GetKlippaInfo";

const SecureIDModal = () => {
  // Charger les ressources externes et initialiser le SDK Klippa
  useEffect(() => {
    // Ajouter le CSS du SDK Klippa
    const styleLink = document.createElement("link");
    styleLink.rel = "stylesheet";
    styleLink.href =
      "https://custom-ocr.klippa.com/sdk/web/build/identity_verification/0.6.3/style.css";
    document.head.appendChild(styleLink);

    // Ajouter le script du SDK Klippa
    const script = document.createElement("script");
    script.src =
      "https://custom-ocr.klippa.com/sdk/web/build/identity_verification/0.6.3/klippa.min.js";
    script.onload = initKlippa;
    document.body.appendChild(script);

    const basePath = "/User";

    // Si le payload kyc est fourni dans l'URL (QR mobile), décoder et stocker
    try {
      const params = new URLSearchParams(window.location.search);
      const kycParam = params.get("kyc");
      const authParam = params.get("auth");
      const docIdParam = params.get("docid");
      if (kycParam) {
        try {
          const decoded = decodeURIComponent(atob(kycParam));
          try {
            const parsed = JSON.parse(decoded);
            if (authParam) parsed.auth = authParam;
            if (docIdParam) parsed.docid = docIdParam;
            localStorage.setItem("kyc_waiting", JSON.stringify(parsed));
          } catch {
            // sauvegarde de secours si le JSON n'est pas parseable
            localStorage.setItem("kyc_waiting", decoded);
          }
          console.log("kyc payload restauré depuis l'URL vers localStorage");
        } catch (e) {
          console.warn("Impossible de décoder le paramètre kyc :", e);
        }
      }
    } catch (e) {
      console.warn(
        "Erreur lors de la lecture des paramètres URL pour kyc :",
        e
      );
    }

    // Récupère l'objet JSON stocké dans localStorage.kcy_waiting si disponible
    function parseLocalKyc() {
      const raw = localStorage.getItem("kyc_waiting");
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }

    // Récupère le docId présent soit dans l'URL soit dans localStorage (kyc_waiting)
    function getDocIdFromUrlOrLocal() {
      const params = new URLSearchParams(window.location.search);
      const docIdFromUrl = params.get("docid");
      if (docIdFromUrl) return docIdFromUrl;
      const localParsed = parseLocalKyc();
      return localParsed?.docid || null;
    }

    // Récupère le token d'auth (Cookie ou local kyc.auth)
    function getAuthToken() {
      const cookieToken = Cookies.get("Token");
      if (cookieToken) return cookieToken;
      const localParsed = parseLocalKyc();
      return localParsed?.auth || null;
    }

    function getSessionIdFromKlippaSession(session) {
      return session?.session?.ID || session?.ID || null;
    }

    function redirectWithStatus(status) {
      if (status === "success") {
        window.location.href = `${basePath}/DefaultLots`;
      } else {
        window.location.href = `${basePath}/DocumentKYC?kycComplete=${status}`;
      }
    }

    // Traitement principal appelé après la fin de la session Klippa
    async function handleKlippaCompletion(session) {
      try {
        const targetDocumentId = getDocIdFromUrlOrLocal();
        if (!targetDocumentId) {
          console.warn(
            "Aucun documentId trouvé dans l'URL ou le localStorage !"
          );
          redirectWithStatus("error");
          return;
        }

        const sessionId = getSessionIdFromKlippaSession(session);
        if (!sessionId) {
          console.warn("Aucun ID de session Klippa trouvé !");
          redirectWithStatus("error");
          return;
        }

        const tokenToUse = getAuthToken();
        if (!tokenToUse) {
          console.warn("Aucun token d'authentification disponible !");
          redirectWithStatus("error");
          return;
        }

        try {
          await webhookKlippa(sessionId, targetDocumentId);
          redirectWithStatus("success");
          return;
        } catch (dlErr) {
          console.warn("Échec de l'appel webhook Klippa :", dlErr);
          redirectWithStatus("error");
          return;
        }
      } catch (err) {
        console.warn("Erreur lors du traitement Klippa :", err);
        redirectWithStatus("error");
      }
    }

    //  Initialisation Klippa 
    let klippaInitialized = false;
    let klippaClickHandler = null;

    function initKlippa() {
      if (klippaInitialized) return;
      klippaInitialized = true;

      const button = document.getElementById("startVerification");
      if (!button) return;

      // Éviter d'ajouter plusieurs fois le même listener (React.StrictMode peut monter 2×)
      if (button.dataset.klippaAttached) return;
      button.dataset.klippaAttached = "1";

      klippaClickHandler = async () => {
        button.disabled = true;
        try {
          const sessionToken = await getSessionTokenKlippa();
          if (!window.KlippaIDVerification) {
            console.error("Le SDK Klippa n'est pas chargé correctement.");
            return;
          }

          window.KlippaIDVerification.init({
            token: sessionToken,
            containerId: "klippa-identity-verification",
            useModal: true,
            language: "fr",
            qrCodeFallback: true,
            enableRealtimeFeedback: true,

            onFinish: async (session) => {
              await handleKlippaCompletion(session);
            },

            onError: (event) => {
              console.error("Erreur Klippa :", event);
              redirectWithStatus("error");
            },
          });
        } finally {
          button.disabled = false;
        }
      };

      button.addEventListener("click", klippaClickHandler);
    }

    // Nettoyage à la sortie du hook
    return () => {
      document.head.removeChild(styleLink);
      document.body.removeChild(script);
      const btn = document.getElementById("startVerification");
      if (btn) {
        if (klippaClickHandler)
          btn.removeEventListener("click", klippaClickHandler);
        delete btn.dataset.klippaAttached;
      }
    };
  }, []);

  // Rendu du modal
  return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <h1>Vérification d'identité</h1>
      <p>
        Cliquer sur le bouton ci-dessous pour commencer la vérification
        d'identité.
      </p>

      <button
        id="startVerification"
        style={{
          padding: "10px 20px",
          backgroundColor: "#5EB66E",
          color: "white",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 16,
        }}
      >
        Démarrer la vérification
      </button>

      <div
        id="klippa-identity-verification"
        style={{ marginTop: 24, minHeight: 400 }}
      ></div>
    </div>
  );
};

export default SecureIDModal;
